import React, { useEffect, useState, Suspense } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import coursesData from "../../data/CourseData";
import InscriptionModal from "../modals/InscriptionModal";
import PromoModal from "../modals/PromoModal";
import WhatsAppButton from "../header/WhatsAppButton";
import { getLocation } from "../utils/GetLocation";
import { trackMetaEvent } from "../utils/MetaPixel";
import { CountdownTimer } from "../utils/CountdownTimer";
import VisitorPopup from "../utils/VisitorPopup";
import PresalePrices from "../utils/PresalePrices";
import UpsellModal from '../modals/UpsellModal';
import logogarantia from "../../assets/img/sello_garantia.webp";
import imgHeader from "../../assets/img/page-header.webp";
import "../../styles/CourseDetail003.css";
import { XCircle } from "lucide-react";


const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // 🔹 Buscamos curso por ID
  const course = coursesData.find((c) => c.id === id);

  const phoneNumber = "573028688273";
  const message = encodeURIComponent(
    `Hola, leí todo el contenido de la página web del "${course.type}" "${course.titleMain}" y quiero inscribirme con la promoción de la página web pero aún tengo dudas y quisiera resolverlas`
  );
  const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${message}`;

  // --- Estados ---
  const [showModal, setShowModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [price, setPrice] = useState({ oldPrice: "", newPrice: "", currency: "" });
  const [country, setCountry] = useState("us");
  const [presalePrices, setPresalePrices] = useState({});

  // Estado para controlar la pestaña activa en la sección "Contenido del Curso"
  const [activeTab, setActiveTab] = useState(1);
  // Estado para controlar qué pregunta del FAQ está abierta
  const [openFAQ, setOpenFAQ] = useState(null);

  // --- Modales ---
  const handleShowModal = (id) => {
    setShowModal(true);
  }
  const handleCloseModal = () => setShowModal(false);
  const handleClosePromoModal = () => setShowPromoModal(false);
  const handleConfirmPromoModal = () => {
    setShowPromoModal(false);
    handleShowModal();
    trackMetaEvent("Lead", { from: "PromoModal", content_name: course.titleMain });
  };

  let countryValues = 'us';

  //Mover el scroll al inicio cuando se carga la página
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const TestimonialsCourse = React.lazy(() => import("./TestimonialsCourse"));

  const [activeModuleId, setActiveModuleId] = useState(course?.modules[0]?.id);

  const activeModule = course?.modules.find(module => module.id === activeModuleId);

  useEffect(() => {
    let timer; // Definir el timer aquí para que sea accesible en la función de limpieza
    // Se asegura de que 'course' se haya cargado antes de continuar
    if (course) {
      const hasSeenModal = sessionStorage.getItem(`promoModalSeen_${course.id}`);
      // Muestra el modal si el curso tiene la opción y no se ha visto en la sesión
      if (course.modalOffer) {
        // Establecer un temporizador para mostrar el modal después de 3 segundos
        timer = setTimeout(() => {
          setShowPromoModal(true);
          sessionStorage.setItem(`promoModalSeen_${course.id}`, 'true');
        }, 15000); // 3000 milisegundos = 3 segundos
      }
    }
  }, [course]); // El efecto depende de que `course` esté disponible


  // --- Precios según ubicación ---
  useEffect(() => {
    getLocation().then(({ countryCode }) => {
      const resolvedCountry = countryCode || 'us';
      setCountry(resolvedCountry);
      countryValues = course.values[countryCode] || course.values["us"]; // Si el país no está definido, usa USD
      setPrice({ oldPrice: countryValues.oldPrice.toLocaleString("de-DE"), newPrice: countryValues.newPrice.toLocaleString("de-DE"), priceInscription: ((countryValues.newPrice) * 0.6).toLocaleString("de-DE"), currency: countryValues.currency, priceFormat: countryValues.newPrice, priceUpsel: countryValues.newPrice * 0.80 });
      setPresalePrices(countryValues?.presale)
    });
  }, [course.values]);

  // --- Evento de vista ---
  useEffect(() => {
    if (course) {
      trackMetaEvent("ViewContent", {
        content_name: course.titleMain,
        content_category: `${course.type} Online`,
        content_ids: [course.id],
      });
    }
  }, [course]);

  // Este hook se ejecuta una vez que el componente se monta en el DOM
  useEffect(() => {
    // Se crea un observador que vigila cuándo los elementos entran en la pantalla
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Si un elemento está visible, se le añade la clase 'is-visible' para activar la animación
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, {
      threshold: 0.1 // La animación se activa cuando al menos el 10% del elemento es visible
    });

    // Se le dice al observador que vigile todos los elementos con la clase 'reveal-on-scroll'
    document.querySelectorAll('.reveal-on-scroll').forEach(el => {
      observer.observe(el);
    });

    // Función de limpieza: se desconecta el observador cuando el componente se desmonta para evitar fugas de memoria
    return () => observer.disconnect();
  }, []); // El array vacío asegura que este efecto se ejecute solo una vez

  if (!course.active) {
    return (
      <>
        {/* Header Section */}
        <div className="w-100 overflow-hidden mb-5 position-relative" style={{ minHeight: '300px' }}>
          <img
            src={imgHeader}
            alt="Encabezado de página curso online"
            className="position-absolute top-0 start-0 w-100 h-100 object-fit-cover"
            style={{ zIndex: 0 }}
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />

          <div className="position-absolute top-0 start-0 w-100 h-100" style={{ background: 'rgba(0, 0, 0, 0.5)', zIndex: 1 }}></div>

          <div className="container position-relative" style={{ zIndex: 2 }}>
            <div className="d-flex flex-column justify-content-center h-100" style={{ minHeight: '300px' }}>
              <h3 className="display-4 text-white text-uppercase">Detalles del curso</h3>
              <div className="d-inline-flex text-white">
                <p className="m-0 text-uppercase">
                  <Link to="/">Inicio</Link>
                </p>
                <i className="fa fa-angle-double-right pt-1 px-3"></i>
                <p className="m-0 text-uppercase">Detalles del curso</p>
              </div>
            </div>
          </div>
        </div>
        <div className="container py-5 text-center">
          <h2 style={{ backgroundColor: "red", color: "white" }}>El curso aún no está disponible, pero muy pronto abriremos inscripciones. 🎉</h2>
          <h3 className="text-center mt-4">Atento al lanzamiento los próximos días para obtener el precio de PREVENTA.</h3>
        </div>
        <div className="text-center">
          <button onClick={() => navigate("/")} className="px-4 py-2 btn-primary">
            IR A LA PÁGINA PRINCIPAL
          </button>
        </div>
      </>
    );
  }

  return (
    <div id="import-course-lp" className="bg-slate-50 text-slate-800">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg fixed top-0 left-0 right-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-blue-700">Dissemp Academy</h1>
          <button
            type="button" // importante para evitar que actúe como submit
            onClick={() => {
              const section = document.querySelector("#benefits");
              if (section) {
                const yOffset = -120; // Ajusta según la altura de tu header
                const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: "smooth" });
              }
            }}
            className="bg-yellow-400 text-slate-900 font-bold py-2 px-6 rounded-full hidden md:inline-block"
          >
            Inscribirme Ahora
          </button>
        </div>
      </header>

      {/* Hook Banner */}
      <div className="bg-red-600 text-white text-center font-bold py-2 text-sm sm:text-base fixed top-[60px] left-0 right-0 z-40 animate-pulse">
        {course.hookMain}
      </div>

      <main className="pt-28 page-course-detail">
        {/* Hero Section */}
        <section className="text-center py-12 md:py-28 px-6 relative" // Eliminamos bg-[#0b1120]
          style={{
            backgroundImage: `linear-gradient(rgba(11, 17, 32, 0), rgba(11, 17, 32, 0.1)), url('${course.bannerSuperior}?q=80&w=2070&auto=format&fit=crop')`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}>
          <div className="max-w-4xl mx-auto relative z-10">

            <h1 className="mt-6 text-4xl md:text-6xl font-extrabold text-[#E0F0FF] leading-tight">
              {course.hook.map((part, index) => (
                <span key={index}>
                  {part.text}{" "}
                </span>
              ))}
            </h1>
            {/* Etiqueta superior */}
            <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold uppercase">
              {course.typeTitle}
            </span>
            <p className="mt-2 text-lg md:text-xl font-semibold text-gray-300 leading-relaxed">
              {course.titleMain}
            </p>
            <button
              type="button" // importante para evitar que actúe como submit
              onClick={() => {
                const section = document.querySelector("#benefits");
                if (section) {
                  const yOffset = -120; // Ajusta según la altura de tu header
                  const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
                  window.scrollTo({ top: y, behavior: "smooth" });
                }
              }}
              className="bg-yellow-400 text-slate-900 font-extrabold py-4 px-10 text-lg rounded-full shadow-xl hover:bg-yellow-500 transition-transform animated-bounce-button"
            >
              {course.botonHookText}
            </button>
            <p className="text-white-300 mt-4 text-sm">Acceso inmediato. 100% online. Estudia a tu ritmo.</p>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <h3 className="text-3xl font-extrabold text-center text-black mb-12">¿Te identificas con alguna de estas situaciones?</h3>
            <h6 className="text-3l font-bold text-center text-green mb-12">Si respondiste "sí" a alguna, estás en el lugar correcto.</h6>

            <div className="max-w-2xl mx-auto space-y-4">
              {course.paintPoints.map((point, i) => (
                <div key={i} className="text-black font-semibold bg-slate-100 p-5 rounded-lg shadow flex items-center space-x-4">
                  <div className="flex items-center justify-center w-8 h-8">
                    <XCircle className="text-red-500 w-7 h-7" strokeWidth={2.5} />
                  </div>
                  <p>{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-slate-100">
          <div className="container mx-auto px-6">
            <h3 className="text-3xl font-bold text-center text-black mb-16">
              Beneficios que no encontrarás en otros cursos
            </h3>
            <h6 className="text-3l font-bold text-center text-green mb-12">Con este {course.type} online obtendrás la ruta exacta y el acompañamiento para lograrlo. Esto es lo que conseguirás:</h6>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {course.benefits.map((benefit, i) => (
                <div key={i} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-blue-100 transition-shadow">

                  <h4 className="text-xl text-gray font-semibold mb-2">{benefit}</h4>

                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- Value Stack / What you get Section --- 
        <section className="py-20 bg-white" id="benefits" >
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              Recibirás Acceso Completo a Todo Esto:
            </h2>

            <div className="max-w-3xl mx-auto mt-12 grid sm:grid-cols-2 gap-6 text-left">
              {course.includedCourse.map((text, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="bg-green-100 text-green-600 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span className="text-slate-700">{text}</span>
                </div>
              ))}
            </div>

            <a type="button" // importante para evitar que actúe como submit
              onClick={() => {
                const section = document.querySelector("#oferta");
                if (section) {
                  const yOffset = -120; // Ajusta según la altura de tu header
                  const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
                  window.scrollTo({ top: y, behavior: "smooth" });
                }
              }} className="inline-block mt-12 bg-yellow-400 text-slate-900 font-extrabold py-4 px-10 text-lg rounded-full shadow-2xl transition-transform hover:scale-105 transform duration-300">
              ¡Quiero Asegurar mi Cupo!
            </a>
          </div>
        </section>
*/}
        {/* Contenedor Principal con Borde Verde */}
        <section className="py-20 bg-white" id="benefits" >

          <div class="flex flex-col md:flex-row items-center justify-between gap-10">

            {/* Columna Izquierda: Texto */}
            <div className="container mx-auto px-10 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                Recibirás Acceso Completo a Todo Esto:
              </h2>

              <div className="max-w-3xl mx-auto mt-12 grid sm:grid-cols-2 gap-6 text-left">
                {course.includedCourse.map((text, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="bg-green-100 text-green-600 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                      </svg>
                    </div>
                    <span className="text-slate-700">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Columna Derecha: Caja 3D del Producto */}
            <div className="flex-2 flex justify-center items-center relative w-full min-h-[180px]">

              {/* El brillo de fondo */}
              <div className="absolute inset-0 glow-effect z-0"></div>

              <img src={course.imgBonus} alt="Bonos del curso" className="w-full pt-8" fetchpriority="high" loading="lazy" />

            </div>
          </div>
          <div className="container mx-auto px-12 text-center">
            <a type="button" // importante para evitar que actúe como submit
              onClick={() => {
                const section = document.querySelector("#oferta");
                if (section) {
                  const yOffset = -120; // Ajusta según la altura de tu header
                  const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
                  window.scrollTo({ top: y, behavior: "smooth" });
                }
              }} className="inline-block mt-2 bg-yellow-400 text-slate-900 font-extrabold py-4 px-10 text-lg rounded-full shadow-2xl transition-transform hover:scale-105 transform duration-300">
              ¡Quiero Asegurar mi Cupo!
            </a>
          </div>
        </section>

        {/* Course Content Section */}
        <section className="py-20 bg-slate-100">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              Contenido del {course.type} completo
            </h2>
            <div className="max-w-5xl mx-auto">
              {/* Las pestañas se generan dinámicamente desde el array 'modules' */}
              <div className="flex flex-wrap justify-center border-b-2 border-slate-200 mb-8 reveal-on-scroll">
                {course.modules.map(module => (
                  <button
                    key={module.id}
                    onClick={() => setActiveModuleId(module.id)}
                    className={`px-4 py-3 text-sm md:text-base md:px-6 border-b-4 transition-colors duration-300 ${activeModuleId === module.id ? 'border-blue-600 text-blue-600 font-bold' : 'text-slate-500 border-transparent hover:text-blue-500'}`}
                  >
                    {module.title.split(':')[0]} {/* Extrae "Módulo X" del título */}
                  </button>
                ))}
              </div>

              {/* El contenido se muestra basado en el módulo activo */}
              <div className="reveal-on-scroll">
                {activeModule && (
                  <div className="bg-white p-8 rounded-lg shadow-md transition-all duration-500">
                    <h4 className="text-2xl font-bold mb-2 text-blue-700">{activeModule.title}</h4>
                    <p className="text-black-600 mb-6">{activeModule.content}</p>
                    <ul className="space-y-3 text-slate-700 list-disc list-inside">
                      {activeModule.lessons.map(lesson => (
                        <li key={lesson.id}>{lesson.title}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="oferta" className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white"
          style={{
            backgroundImage: `linear-gradient(rgba(11, 17, 32, 0), rgba(11, 17, 32, 0.1)), url('${course.bannerInferior}?q=80&w=2070&auto=format&fit=crop')`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}>
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 text-center text-slate-900">
              <h3 className="text-4xl font-extrabold text-slate-900 mb-4">Accede al {course.type} Completo</h3>
              <p className="text-slate-600 text-lg mb-8">ÚNICA inversión para una transformación profesional completa.</p>
              <div className="text-slate-600 text-lg mb-4" style={{ fontSize: "15px", color: "red", fontWeight: "bold" }}>¡SI ESTAS CON VPN DESCONECTATE PARA VER PRECIOS DE TU PAIS!</div>
              <p className="text-lg text-slate-600 mb-6">{course.offerText}</p>
              <p className="text-2xl text-red-500 line-through">Antes: {price.oldPrice} {price.currency}</p>
              <p className="text-5xl font-bold text-blue-700">Hoy: {price.newPrice} {price.currency}</p>
              <p className="mt-4 bg-green-100 text-green-800 font-bold py-2 px-4 rounded-lg inline-block">
                ¡Estas ahorrando {(parseInt(price.oldPrice.replace(/\./g, '')) - parseInt(price.newPrice.replace(/\./g, ''))).toLocaleString('es-CO')} {price.currency}!
              </p>
              <button
                onClick={() => {
                  handleShowModal();
                  trackMetaEvent('InitiateCheckout', { content_name: course.title, content_ids: [course.id], currency: price.currency, value: price.newPrice })
                }
                }
                className="bg-yellow-400 text-slate-900 font-extrabold py-4 px-10 text-lg rounded-full shadow-xl hover:bg-yellow-500 transition-transform animated-bounce-button"
              >
                ¡Inscribirme Ahora con Descuento!
              </button>
              <div className="mt-10 bg-red-100 text-center text-red-800 font-bold py-2 px-4 rounded-lg inline-block" style={{ fontSize: "140%" }}>
                {course.SenseUrgency}
              </div>
            </div>

            <div className="mt-10 text-center">
              {course.emergencyStopwatch && <CountdownTimer />}
            </div>
          </div>
        </section>

        {/* GARANTÍA */}
        {course.id != "10" && (
          <section className="py-20 bg-slate-100">
            <div className="container mx-auto px-6">
              <h3 className="text-3xl font-bold text-center text-black mb-16">
                🎯 Garantía de excelencia
              </h3>
              <div className="garantia-container">
                <div className="garantia-text">
                  <h4 style={{ color: 'black', fontSize: '1.125rem', marginTop: '1rem', textAlign: 'justify' }}>
                    Con nuestro {course.type} y el Sello de Excelencia de 5 Días, no solo aprenderás, sino con total confianza.
                  </h4>
                  <h6 style={{ color: 'black', fontSize: '1.125rem', marginTop: '1rem', textAlign: 'justify' }}>
                    💡 Si en los primeros 5 días te arrepientes de tu compra, te devolvemos el 80% de tu inversión. Así de fácil.
                  </h6>
                  <h6 style={{ color: 'black', fontSize: '1.125rem', marginTop: '1rem', textAlign: 'justify' }}>
                    🔐 Aprende sin miedo. Invierte sin riesgo.
                  </h6>
                  <h6 style={{ color: 'black', fontSize: '1.125rem', marginTop: '1rem', textAlign: 'justify' }}>
                    🔥 Cientos de alumnos ya comenzaron… ¿y tú?
                  </h6>
                </div>
                <div className="garantia-img">
                  <img
                    src={logogarantia}
                    alt="Sello 7 días de garantía"
                    style={{ width: '100%', maxWidth: '300px', height: 'auto' }}
                  />
                </div>
              </div>
            </div>
          </section>)}

        {/* Instructor */}
        <section className="py-20 bg-white">
          <h3 className="text-3xl font-bold text-center text-black mb-16">
            Tu instructor de confianza
          </h3>
          <div className="container mx-auto px-6 max-w-4xl grid md:grid-cols-3 gap-12 items-center">
            <div className="text-center">
              <img src={course.instructor.image} alt={course.instructor.name} className="w-48 h-48 rounded-full mx-auto" />
            </div>
            <div className="md:col-span-2">
              <h3 className="text-3xl font-bold text-gray-600">{course.instructor.name}</h3>
              <p className="mt-4 text-gray-600">{course.instructor.bio}</p>
              <p className="text-slate-600 font-semibold">"Estoy aquí para guiarte en cada paso de este viaje intensivo y emocionante."</p>
            </div>
          </div>
        </section>

        {/* Testimonial */}
        {course.id === "1" && (
          <Suspense fallback={<div>Cargando testimonios...</div>}>
            <TestimonialsCourse course={course} />
          </Suspense>
        )}

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6 max-w-3xl">
            <h3 className="text-3xl font-bold text-center mb-12 text-black">Preguntas Frecuentes</h3>
            {course.faq.map((item, i) => (
              <div key={i} className="mb-4 bg-white rounded-lg shadow">
                <button
                  className="w-full flex justify-between p-4 font-semibold text-gray"
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                >
                  {item.q}
                  <span>{openFAQ === i ? "▲" : "▼"}</span>
                </button>
                {openFAQ === i && <div className="p-4 text-slate-600 text-gray">{item.a}</div>}
              </div>
            ))}
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white text-center py-10">
        <h3 className="text-2xl font-bold mb-4">¿Preparados para dar el paso?</h3>
        <button onClick={() => {
          handleShowModal();
          trackMetaEvent('InitiateCheckout', { content_name: course.title, content_ids: [course.id], currency: price.currency, value: price.newPrice })
        }
        } className="bg-yellow-400 text-slate-900 font-bold py-4 px-10 rounded-full">
          ¡SÍ, QUIERO INSCRIBIRME!
        </button>
        <div className="container mx-auto px-6 py-12 text-center">
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4">No, aun tengo dudas y quiero resolverlas</h3>
            <p className="text-slate-300 mb-6">Contáctanos por WhatsApp, estamos para ayudarte.</p>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button bg-green-500 text-white font-bold py-3 px-8 rounded-full inline-flex items-center hover:bg-green-600"
              style={{ boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)', width: '60%' }}
              onClick={() => trackMetaEvent('Lead', { content_name: course.title, content_ids: [course.id] })}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.886-.001 2.269.655 4.398 1.898 6.166l-1.29 4.721 4.773-1.241z" /></svg>
              Chatear por WhatsApp
            </a>
          </div>
          <div className="text-slate-300 mt-4"><h1>Transformando futuros a través de la educación</h1></div>
        </div>
      </footer>

      {/* Botón flotante de WhatsApp */}
      <WhatsAppButton course={course} />

      {/* Modales */}
      {
        showPromoModal && (
          <PromoModal onClose={handleClosePromoModal} onConfirm={handleConfirmPromoModal} course={course} newPrice={price.newPrice} oldPrice={price.oldPrice} currency={price.currency} />
        )
      }
      <VisitorPopup />
      <InscriptionModal show={showModal} handleClose={handleCloseModal} course={course} newPrice={price.newPrice} currency={price.currency} country={country} />
    </div >
  );
};

export default CourseDetail;
